import CardFormModal from '@/components/cards/CardFormModal'
import CardTile from '@/components/cards/CardTile'
import EmptyState from '@/components/ui/EmptyState'
import ProfileFormModal from '@/components/profile/ProfileFormModal'
import SocialLinks from '@/components/profile/SocialLinks'
import { useAuthModal } from '@/contexts/AuthModalContext'
import { useAuth } from '@/hooks/useAuth'
import type { Card } from '@/hooks/useCards'
import { useCards } from '@/hooks/useCards'
import type { Profile } from '@/hooks/useProfile'
import { useProfile } from '@/hooks/useProfile'
import { getStorePath, getStoreUrlPreview } from '@/lib/storeSlug'
import { COLOR_SCHEMES, STORE_LAYOUTS, getColorScheme, getStoreLayout, getStoreTheme } from '@/lib/storeTheme'
import {Pencil, Plus, Sparkles, Store as StoreIcon} from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

function Dashboard() {
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const ownerId = isAuthenticated ? user?.userId : undefined
  const { profile, loading: profileLoading, saveProfile } = useProfile(ownerId)
  const { cards, loading: cardsLoading, createCard, updateCard, deleteCard } = useCards(ownerId)
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const [cardModalOpen, setCardModalOpen] = useState(false)
  const [editingCard, setEditingCard] = useState<Card | null>(null)

  if (!isAuthenticated) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-4 text-center">
        <div className="mb-4 rounded-full bg-[#3B4CCA] p-4 shadow-[0_5px_0_#24308a]">
          <StoreIcon className="h-6 w-6 text-[#FFDE00]" />
        </div>
        <h1 className="font-display text-3xl font-extrabold text-white">Sign in to manage your store</h1>
        <p className="mt-2 text-sm font-semibold text-white/55">
          Join the open beta, name your shop, and start posting singles or sealed product with TCGplayer and eBay links.
        </p>
        <button
          type="button"
          onClick={openAuth}
          className="mt-6 rounded-full bg-[#FFDE00] px-6 py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1"
        >
          Get started
        </button>
      </div>
    )
  }

  const handleSaveProfile = async (data: Partial<Profile>) => {
    try {
      await saveProfile(data)
      toast.success('Store updated')
    } catch {
      toast.error('Could not save your store. Please try again.')
    }
  }

  const openNewCard = () => {
    setEditingCard(null)
    setCardModalOpen(true)
  }

  const handleSaveCard = async (data: Partial<Card>) => {
    try {
      if (editingCard) {
        await updateCard(editingCard._id, data)
        toast.success('Listing updated')
      } else {
        await createCard(data)
        toast.success('Listing added to your store')
      }
    } catch {
      toast.error('Could not save this listing. Please try again.')
    }
  }

  const handleDeleteCard = async (id: string) => {
    if (!window.confirm('Remove this listing from your store?')) return
    try {
      await deleteCard(id)
      toast.success('Listing removed')
    } catch {
      toast.error('Could not remove this listing. Please try again.')
    }
  }

  if (profileLoading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="h-40 animate-pulse rounded-[32px] bg-white/5" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-4 text-center">
        <div className="mb-4 rounded-full bg-[#FFDE00] p-4 shadow-[0_5px_0_#c9a800]">
          <StoreIcon className="h-6 w-6 text-black" />
        </div>
        <div className="mb-3 inline-flex items-center rounded-full bg-[#FF1A1A] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white">
          Open beta
        </div>
        <h1 className="font-display text-3xl font-extrabold text-white">Set up your store</h1>
        <p className="mt-2 text-sm font-semibold text-white/55">
          Pick a shop name to get your public link, like mycardshop.online/MyStoreName. The open beta is free.
        </p>
        <button
          type="button"
          onClick={() => setProfileModalOpen(true)}
          className="mt-6 rounded-full bg-[#FFDE00] px-6 py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1"
        >
          Create store profile
        </button>
        <ProfileFormModal
          open={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          onSubmit={handleSaveProfile}
          initial={profile}
        />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="mb-4 inline-flex items-center rounded-full border-2 border-[#FFDE00] bg-[#FFDE00] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-black">
        Free Open Beta
      </div>
      <div className="overflow-hidden rounded-[32px] border-4 shadow-[10px_10px_0_rgba(255,26,26,0.22)]" style={{ borderColor: getStoreTheme(profile?.colorScheme).secondary, backgroundColor: `${getStoreTheme(profile?.colorScheme).surface}cc` }}>
        <div className="relative h-36 w-full overflow-hidden rounded-t-[28px] sm:h-44">
          <div className="absolute inset-0 bg-transparent">
            {profile?.backgroundUrl && !profile?.bannerUrl ? <img src={profile.backgroundUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" /> : null}
            {profile?.bannerUrl ? (
              <>
                <img src={profile.bannerUrl} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </>
            ) : null}
          </div>
          <div className="absolute bottom-3 left-4 z-10 h-20 w-20 overflow-hidden rounded-full border-4 bg-[#111111] shadow-[0_8px_24px_rgba(0,0,0,0.4)] sm:bottom-4 sm:left-5 sm:h-24 sm:w-24" style={{ borderColor: getStoreTheme(profile?.colorScheme).accent }}>
            {profile?.avatarUrl ? (
              <img src={profile.avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[#FF1A1A] text-xl font-extrabold text-white">
                {(profile?.storeName || user?.userName || '?')[0]?.toUpperCase()}
              </div>
            )}
          </div>
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="font-display break-words text-2xl font-extrabold text-white">{profile?.storeName || 'My Pokemon Store'}</h1>
              {profile?.storeName ? (
                <p className="mt-1 break-all text-sm font-extrabold text-[#FFDE00]">{getStoreUrlPreview(profile.storeName, profile.storeSlug)}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Link
                to={getStorePath(profile || { userId: user?.userId })}
                className="rounded-full border-2 border-white/15 px-3.5 py-2 text-center text-xs font-extrabold text-white/80 transition-transform hover:-translate-y-0.5 hover:border-white/30 hover:text-white"
              >
                View public store
              </Link>
              <button
                type="button"
                onClick={() => setProfileModalOpen(true)}
                className="flex items-center justify-center gap-1.5 rounded-full bg-[#3B4CCA] px-3.5 py-2 text-xs font-extrabold text-white shadow-[0_3px_0_#24308a] transition-transform hover:-translate-y-0.5"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit store
              </button>
            </div>
          </div>
          <div className="mt-1 min-w-0">
            <section className="mt-4">
              <h2 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/40">Bio</h2>
              {profile?.bio ? (
                <p className="mt-2 max-w-md break-words text-sm text-white/70">{profile.bio}</p>
              ) : (
                <p className="mt-2 text-sm text-white/35">Add a bio so collectors know what you&apos;re about.</p>
              )}
            </section>
            <div className="mt-4 flex flex-wrap gap-1.5">
              <span className="rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide" style={{ borderColor: `${getStoreTheme(profile?.colorScheme).accent}59`, backgroundColor: `${getStoreTheme(profile?.colorScheme).accent}1a`, color: getStoreTheme(profile?.colorScheme).accent }}>
                {STORE_LAYOUTS.find(option => option.value === getStoreLayout(profile?.storeLayout))?.label || 'Classic grid'}
              </span>
              <span className="rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide" style={{ borderColor: `${getStoreTheme(profile?.colorScheme).secondary}80`, backgroundColor: `${getStoreTheme(profile?.colorScheme).secondary}26`, color: '#fff' }}>
                {COLOR_SCHEMES.find(option => option.value === getColorScheme(profile?.colorScheme))?.label || 'Classic Pokemon'}
              </span>
            </div>
            {profile?.favoriteGames?.length ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {profile.favoriteGames.map(g => (
                  <span key={g} className="rounded-full border-2 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide" style={{ borderColor: `${getStoreTheme(profile?.colorScheme).accent}59`, backgroundColor: `${getStoreTheme(profile?.colorScheme).accent}26`, color: getStoreTheme(profile?.colorScheme).accent }}>
                    {g}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="mt-3">
              <SocialLinks
                size="sm"
                links={{
                  tiktokUrl: profile?.tiktokUrl,
                  xUrl: profile?.xUrl,
                  facebookUrl: profile?.facebookUrl,
                  instagramUrl: profile?.instagramUrl,
                  websiteUrl: profile?.websiteUrl,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-2xl font-extrabold text-white">
          Your listings <span className="text-white/40">({cards.length})</span>
        </h2>
        <button
          type="button"
          onClick={openNewCard}
          className="flex w-fit items-center gap-1.5 rounded-full bg-[#FFDE00] px-4 py-2 text-xs font-extrabold text-black shadow-[0_4px_0_#c9a800] transition-transform hover:-translate-y-0.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Add listing
        </button>
      </div>

      {cardsLoading ? (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-white/5" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            icon={Sparkles}
            title="No listings yet"
            description="Upload a Pokemon single or sealed product and attach TCGplayer or eBay links so collectors can buy from you."
            action={(
              <button
                type="button"
                onClick={openNewCard}
                className="rounded-full bg-[#FFDE00] px-4 py-2 text-sm font-extrabold text-black shadow-[0_4px_0_#c9a800]"
              >
                Add your first listing
              </button>
            )}
          />
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {cards.map(card => (
            <CardTile
              key={card._id}
              card={card}
              showOwnerActions
              onEdit={() => { setEditingCard(card); setCardModalOpen(true) }}
              onDelete={() => handleDeleteCard(card._id)}
            />
          ))}
        </div>
      )}

      <ProfileFormModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        onSubmit={handleSaveProfile}
        initial={profile}
      />
      <CardFormModal
        open={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        onSubmit={handleSaveCard}
        initial={editingCard}
      />
    </div>
  )
}

export default Dashboard
